import { useEffect, useMemo, useRef, useState } from "react";
import {
  Play, Pause, Volume2, VolumeX, Maximize, Rewind, FastForward,
  Clapperboard, Film, Gauge, Clock, DollarSign, ShieldCheck, Captions,
  Pencil, Save, Trash2, X,
} from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { GlassCard, Pill, cx } from "@/components/shared";

const fmt = (s) => {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

export default function PreviewPlayer({ proj, onChange }) {
  const a = proj.artifacts || {};
  const scenes = useMemo(() => a.storyboard?.scenes || [], [a.storyboard]);
  const images = useMemo(() => a.scene_images?.images || [], [a.scene_images]);
  const videos = useMemo(() => a.scene_videos?.videos || [], [a.scene_videos]);
  const voiceover = a.voiceover;
  const music = a.music;
  const [cues, setCues] = useState(a.subtitles?.cues || []);
  const [wmPlan, setWmPlan] = useState({});
  const [editingCaps, setEditingCaps] = useState(false);
  const [savingCaps, setSavingCaps] = useState(false);
  useEffect(() => { setCues(a.subtitles?.cues || []); }, [a.subtitles]);
  useEffect(() => {
    const aspect = (proj.config?.aspect_ratio === "9:16") ? "9:16" : "16:9";
    api.get(`/projects/${proj.id}/watermark-plan?aspect=${aspect}`)
      .then(({ data }) => {
        const m = {};
        (data.scenes || []).forEach((s) => { m[s.index] = s; });
        setWmPlan(m);
      }).catch((e) => console.error("[Preview] watermark-plan failed", e));
  }, [proj.id, proj.config]);
  const quality = a.quality;
  const clipsMode = a.scene_videos?.mode;
  const clipsProvider = a.scene_videos?.provider;

  const imgMap = useMemo(() => Object.fromEntries(images.map((i) => [i.index, i.url])), [images]);
  const vidMap = useMemo(() => Object.fromEntries(videos.map((v) => [v.index, v])), [videos]);

  // Ordered clip list
  const clips = useMemo(() => {
    const src = scenes.length ? scenes : images.map((i) => ({ index: i.index, title: i.title }));
    return src.map((s) => ({
      index: s.index,
      title: s.title,
      image: imgMap[s.index] || null,
      video: vidMap[s.index]?.url || null,
      status: vidMap[s.index]?.status,
      nominal: Number(s.duration_seconds) || 8,
    })).sort((x, y) => x.index - y.index);
  }, [scenes, images, imgMap, vidMap]);

  const hasVideo = clips.some((c) => c.video);

  const [durations, setDurations] = useState(() => clips.map((c) => c.nominal));
  useEffect(() => { setDurations(clips.map((c) => c.nominal)); }, [clips.length]); // eslint-disable-line

  const [cur, setCur] = useState(0);
  const [clipTime, setClipTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [showCaps, setShowCaps] = useState(true);

  const videoRef = useRef(null);
  const voiceRef = useRef(null);
  const musicRef = useRef(null);
  const containerRef = useRef(null);
  const rafRef = useRef(null);
  const lastTsRef = useRef(null);

  const offsets = useMemo(() => {
    const o = [0];
    for (let i = 0; i < durations.length; i++) o.push(o[i] + durations[i]);
    return o;
  }, [durations]);
  const total = offsets[offsets.length - 1] || 0;
  const globalTime = (offsets[cur] || 0) + clipTime;

  const musicVol = (music?.volume === "low" ? 0.15 : 0.4);

  // keep audio elements synced with master controls
  useEffect(() => {
    [voiceRef.current, musicRef.current, videoRef.current].forEach((el) => {
      if (!el) return;
      el.muted = muted;
      el.playbackRate = speed;
    });
    if (voiceRef.current) voiceRef.current.volume = volume;
    if (musicRef.current) musicRef.current.volume = volume * musicVol;
  }, [muted, speed, volume, musicVol, cur, playing]);

  const syncAudioTo = (gt) => {
    if (voiceRef.current && isFinite(gt)) {
      try { voiceRef.current.currentTime = Math.min(gt, (voiceRef.current.duration || gt)); } catch {}
    }
  };

  const startAudio = () => {
    if (voiceRef.current?.src) voiceRef.current.play().catch(() => {});
    if (musicRef.current?.src) musicRef.current.play().catch(() => {});
  };
  const pauseAudio = () => {
    voiceRef.current?.pause();
    musicRef.current?.pause();
  };

  // image-mode ticker
  useEffect(() => {
    if (!playing || hasVideo) { cancelAnimationFrame(rafRef.current); lastTsRef.current = null; return; }
    const tick = (ts) => {
      if (lastTsRef.current == null) lastTsRef.current = ts;
      const dt = ((ts - lastTsRef.current) / 1000) * speed;
      lastTsRef.current = ts;
      setClipTime((t) => {
        const nt = t + dt;
        if (nt >= (durations[cur] || 8)) { advance(); return 0; }
        return nt;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, hasVideo, cur, speed, durations]); // eslint-disable-line

  const advance = () => {
    setCur((c) => {
      if (c + 1 >= clips.length) { setPlaying(false); pauseAudio(); return c; }
      return c + 1;
    });
    setClipTime(0);
  };

  const togglePlay = () => {
    if (!clips.length) return;
    if (playing) {
      setPlaying(false);
      videoRef.current?.pause();
      pauseAudio();
    } else {
      setPlaying(true);
      if (hasVideo) videoRef.current?.play().catch(() => {});
      syncAudioTo(globalTime);
      startAudio();
    }
  };

  // when clip changes, load & autoplay if playing
  useEffect(() => {
    const v = videoRef.current;
    if (v && clips[cur]?.video) {
      v.load();
      if (playing) v.play().catch(() => {});
    }
    syncAudioTo((offsets[cur] || 0) + clipTime);
  }, [cur]); // eslint-disable-line

  const onSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const gt = ratio * total;
    let idx = 0;
    while (idx < durations.length - 1 && gt >= offsets[idx + 1]) idx++;
    const local = gt - offsets[idx];
    setCur(idx);
    setClipTime(local);
    if (hasVideo && videoRef.current) {
      setTimeout(() => { try { videoRef.current.currentTime = local; } catch {} }, 30);
    }
    syncAudioTo(gt);
  };

  const jumpTo = (idx) => {
    setCur(idx);
    setClipTime(0);
    syncAudioTo(offsets[idx] || 0);
    if (hasVideo && videoRef.current) setTimeout(() => { try { videoRef.current.currentTime = 0; } catch {} }, 30);
  };

  const skip = (delta) => {
    const gt = Math.min(total, Math.max(0, globalTime + delta));
    let idx = 0;
    while (idx < durations.length - 1 && gt >= offsets[idx + 1]) idx++;
    setCur(idx);
    setClipTime(gt - offsets[idx]);
    if (hasVideo && videoRef.current) setTimeout(() => { try { videoRef.current.currentTime = gt - offsets[idx]; } catch {} }, 30);
    syncAudioTo(gt);
  };

  const toggleFs = () => {
    const el = containerRef.current;
    if (!document.fullscreenElement) el?.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  const activeCue = cues.find((c) => globalTime >= c.start && globalTime < c.end);
  const clip = clips[cur] || {};

  const editCue = (i, field, val) => {
    setCues((arr) => arr.map((c, idx) => idx === i ? { ...c, [field]: field === "text" ? val : Number(val) } : c));
  };
  const delCue = (i) => setCues((arr) => arr.filter((_, idx) => idx !== i));
  const saveCaps = async () => {
    setSavingCaps(true);
    try {
      await api.put(`/projects/${proj.id}/subtitles`, { cues });
      toast.success("Captions saved — preview updated");
      setEditingCaps(false);
      onChange && onChange();
    } catch (e) { toast.error("Could not save captions"); }
    finally { setSavingCaps(false); }
  };

  if (!clips.length) {
    return <GlassCard className="p-8 text-center text-slate-500" data-testid="preview-empty">
      Nothing to preview yet. Generate a project first — scenes, voice and clips appear here as a playable film.
    </GlassCard>;
  }

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="preview-player">
      <div className="lg:col-span-2 space-y-3">
        <div ref={containerRef} className="relative rounded-2xl overflow-hidden bg-black border border-white/10">
          {/* Stage */}
          <div className="aspect-video bg-black grid place-items-center relative">
            {clip.video ? (
              <video
                ref={videoRef}
                key={clip.video}
                src={mediaUrl(clip.video)}
                className="w-full h-full object-contain bg-black"
                playsInline
                data-testid="preview-video-el"
                onLoadedMetadata={(e) => {
                  const d = e.currentTarget.duration;
                  if (isFinite(d) && d > 0) setDurations((arr) => { const n = [...arr]; n[cur] = d; return n; });
                }}
                onTimeUpdate={(e) => setClipTime(e.currentTarget.currentTime)}
                onEnded={advance}
              />
            ) : clip.image ? (
              <img src={mediaUrl(clip.image)} alt={clip.title} className="w-full h-full object-contain" />
            ) : (
              <div className="text-slate-600 flex flex-col items-center gap-2"><Film className="w-8 h-8" /> Scene {clip.index}</div>
            )}

            {/* Subtitles */}
            {showCaps && activeCue && (
              <div className="absolute bottom-4 left-0 right-0 px-6 text-center pointer-events-none" data-testid="preview-subtitle">
                <span className="inline-block bg-black/70 text-white text-sm sm:text-base px-3 py-1.5 rounded-md leading-snug">
                  {activeCue.text}
                </span>
              </div>
            )}

            {/* big play button overlay */}
            {!playing && (
              <button onClick={togglePlay} data-testid="preview-play-overlay"
                className="absolute inset-0 grid place-items-center bg-black/20 hover:bg-black/30 transition">
                <span className="w-16 h-16 rounded-full bg-white/90 grid place-items-center">
                  <Play className="w-7 h-7 text-black ml-1" />
                </span>
              </button>
            )}

            <div className="absolute top-3 left-3 flex gap-2">
              <Pill tone="slate">Scene {clip.index} / {clips.length}</Pill>
              {clip.image && !clip.video && <Pill tone="gold">Image preview</Pill>}
            </div>

            {/* Brand watermark — SAME plan as export (variant/corner/opacity/size per scene) */}
            {(() => {
              const wp = wmPlan[clip.index] || { variant: "white", corner: "br", opacity: 0.15, size_pct: 0.025 };
              const pos = { br: { right: "2%", bottom: "3%" }, bl: { left: "2%", bottom: "3%" },
                            tr: { right: "2%", top: "3%" }, tl: { left: "2%", top: "3%" } }[wp.corner] || { right: "2%", bottom: "3%" };
              return (
                <img src={`/brand/watermark_${wp.variant}.png`} alt="" aria-hidden="true"
                  className="absolute pointer-events-none select-none"
                  style={{ ...pos, width: `${(wp.size_pct * 100).toFixed(2)}%`, opacity: wp.opacity }}
                  data-testid="preview-watermark" />
              );
            })()}
          </div>

          {/* Timeline */}
          <div className="px-3 pt-3">
            <div className="relative h-2.5 rounded-full bg-white/10 cursor-pointer group" onClick={onSeek} data-testid="preview-timeline">
              <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#5B4DFF] to-[#FFB300]"
                style={{ width: `${total ? (globalTime / total) * 100 : 0}%` }} />
              {/* scene markers */}
              {offsets.slice(1, -1).map((o, i) => (
                <div key={i} className="absolute top-0 bottom-0 w-px bg-white/30" style={{ left: `${(o / total) * 100}%` }} />
              ))}
              <div className="absolute -top-1 w-4 h-4 rounded-full bg-white shadow -translate-x-1/2"
                style={{ left: `${total ? (globalTime / total) * 100 : 0}%` }} />
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-3 p-3 flex-wrap">
            <button onClick={() => skip(-5)} className="text-slate-300 hover:text-white" data-testid="preview-rewind"><Rewind className="w-5 h-5" /></button>
            <button onClick={togglePlay} className="w-10 h-10 rounded-full bg-white text-black grid place-items-center hover:scale-105 transition" data-testid="preview-playpause">
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>
            <button onClick={() => skip(5)} className="text-slate-300 hover:text-white" data-testid="preview-forward"><FastForward className="w-5 h-5" /></button>

            <span className="text-xs font-mono text-slate-300" data-testid="preview-time">{fmt(globalTime)} / {fmt(total)}</span>

            <div className="flex items-center gap-2 ml-auto">
              <button onClick={() => setMuted((m) => !m)} className="text-slate-300 hover:text-white" data-testid="preview-mute">
                {muted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
              <input type="range" min="0" max="1" step="0.05" value={muted ? 0 : volume}
                onChange={(e) => { setVolume(Number(e.target.value)); setMuted(false); }}
                className="w-20 accent-[#5B4DFF]" data-testid="preview-volume" />

              <div className="relative">
                <select value={speed} onChange={(e) => setSpeed(Number(e.target.value))} data-testid="preview-speed"
                  className="bg-white/5 border border-white/10 rounded-lg text-xs text-slate-200 px-2 py-1 outline-none">
                  <option value={0.5} className="bg-[#0A1435]">0.5x</option>
                  <option value={1} className="bg-[#0A1435]">1x</option>
                  <option value={1.25} className="bg-[#0A1435]">1.25x</option>
                  <option value={1.5} className="bg-[#0A1435]">1.5x</option>
                  <option value={2} className="bg-[#0A1435]">2x</option>
                </select>
              </div>

              <button onClick={() => setShowCaps((c) => !c)} title="Toggle subtitles"
                className={cx("hover:text-white", showCaps ? "text-[#FFB300]" : "text-slate-400")} data-testid="preview-captions">
                <Captions className="w-5 h-5" />
              </button>
              <button onClick={toggleFs} className="text-slate-300 hover:text-white" data-testid="preview-fullscreen"><Maximize className="w-5 h-5" /></button>
            </div>
          </div>

          {/* hidden audio tracks */}
          {voiceover?.url && <audio ref={voiceRef} src={mediaUrl(voiceover.url)} preload="auto" />}
          {music?.url && <audio ref={musicRef} src={music.url} preload="auto" loop />}
        </div>

        {/* meta strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetaCard icon={ShieldCheck} label="Quality" value={quality?.overall != null ? `${quality.overall}/100` : "—"} />
          <MetaCard icon={Clapperboard} label="Render" value={clip.video ? (clip.status === "rendering" ? "Rendering" : "Rendered") : "Image"} />
          <MetaCard icon={Gauge} label="Mode" value={clipsMode || proj.config?.render_mode || "—"} />
          <MetaCard icon={Clock} label="Duration" value={fmt(total)} />
        </div>

        {/* caption editor */}
        <GlassCard className="p-4" data-testid="caption-editor">
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300] flex items-center gap-2">
              <Captions className="w-3.5 h-3.5" /> Subtitles {cues.length ? `· ${cues.length} cues` : "· none"}
            </div>
            {cues.length > 0 && (editingCaps ? (
              <div className="flex gap-2">
                <button onClick={saveCaps} disabled={savingCaps} data-testid="caption-save"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-emerald-500 text-white hover:bg-emerald-400 disabled:opacity-50">
                  <Save className="w-3.5 h-3.5" /> Save
                </button>
                <button onClick={() => { setCues(a.subtitles?.cues || []); setEditingCaps(false); }} data-testid="caption-cancel"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 text-slate-300">
                  <X className="w-3.5 h-3.5" /> Cancel
                </button>
              </div>
            ) : (
              <button onClick={() => setEditingCaps(true)} data-testid="caption-edit"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 text-slate-300 hover:text-white">
                <Pencil className="w-3.5 h-3.5" /> Edit captions
              </button>
            ))}
          </div>
          {!cues.length ? (
            <p className="text-xs text-slate-500">No subtitles for this project yet.</p>
          ) : editingCaps ? (
            <div className="space-y-2 max-h-[320px] overflow-auto pr-1">
              {cues.map((c, i) => (
                <div key={i} className="flex items-start gap-2" data-testid={`caption-row-${i}`}>
                  <div className="flex flex-col gap-1 w-20 shrink-0">
                    <input type="number" step="0.1" value={c.start} onChange={(e) => editCue(i, "start", e.target.value)}
                      className="bg-white/5 border border-white/10 rounded px-2 py-1 text-[11px] text-slate-200 outline-none" data-testid={`caption-start-${i}`} />
                    <input type="number" step="0.1" value={c.end} onChange={(e) => editCue(i, "end", e.target.value)}
                      className="bg-white/5 border border-white/10 rounded px-2 py-1 text-[11px] text-slate-200 outline-none" data-testid={`caption-end-${i}`} />
                  </div>
                  <textarea value={c.text} onChange={(e) => editCue(i, "text", e.target.value)} rows={2}
                    className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-xs text-slate-200 outline-none resize-none focus:border-[#5B4DFF]/60" data-testid={`caption-text-${i}`} />
                  <button onClick={() => delCue(i)} className="text-slate-500 hover:text-red-400 mt-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-1 max-h-[200px] overflow-auto pr-1">
              {cues.map((c, i) => (
                <div key={i} className={cx("text-xs flex gap-2", activeCue === c ? "text-[#FFB300]" : "text-slate-400")}>
                  <span className="font-mono text-slate-600 shrink-0">{fmt(c.start)}</span> {c.text}
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      </div>

      {/* Side: clip list + tracks */}
      <div className="space-y-3">
        <GlassCard className="p-4">
          <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300] mb-3">Storyboard · Clips</div>
          <div className="space-y-2 max-h-[360px] overflow-auto pr-1">
            {clips.map((c, i) => (
              <button key={c.index} onClick={() => jumpTo(i)} data-testid={`preview-clip-${c.index}`}
                className={cx("w-full flex items-center gap-3 p-2 rounded-lg border text-left transition",
                  i === cur ? "bg-[#5B4DFF]/20 border-[#5B4DFF]/50" : "bg-white/5 border-white/10 hover:border-white/20")}>
                <div className="w-16 h-10 rounded bg-black/40 overflow-hidden shrink-0">
                  {c.image ? <img src={mediaUrl(c.image)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-slate-600"><Film className="w-4 h-4" /></div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-white truncate">Scene {c.index}: {c.title}</div>
                  <div className="text-[10px] text-slate-500">{c.video ? "clip" : "image"} · {Math.round(durations[i] || c.nominal)}s</div>
                </div>
              </button>
            ))}
          </div>
        </GlassCard>

        <GlassCard className="p-4 space-y-3">
          <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300]">Audio tracks</div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-300 flex items-center gap-2"><Volume2 className="w-4 h-4 text-[#5B4DFF]" /> Narration</span>
            <span className="text-slate-500">{voiceover?.url ? `${voiceover.voice} · ${voiceover.speed}x` : "none"}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-300 flex items-center gap-2"><Volume2 className="w-4 h-4 text-[#FFB300]" /> Music</span>
            <span className="text-slate-500">{music?.title ? `${music.title}${music.volume === "low" ? " · low" : ""}` : "none"}</span>
          </div>
          {clipsProvider && <div className="text-[10px] text-slate-600 pt-1">Clips rendered by {clipsProvider}</div>}
        </GlassCard>
      </div>
    </div>
  );
}

function MetaCard({ icon: Icon, label, value }) {
  return (
    <GlassCard className="p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-400 font-mono">
        <Icon className="w-3.5 h-3.5 text-[#5B4DFF]" /> {label}
      </div>
      <div className="text-white font-semibold mt-1 text-sm">{value}</div>
    </GlassCard>
  );
}
